<template>
  <div class="gearchanges overflow-y-hidden">
    <div class="gearchanges__wrapper">
      <v-toolbar elevation="1" color="primary" class="gearchanges__toolbar white--text pa-0 text-lg-h6 font-weight-regular" dark dense>
        <v-toolbar-title>Gear Changes</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon small @click="close"><v-icon size="20">mdi-close</v-icon></v-btn>
      </v-toolbar>
      <v-container>
        <header class="gearchanges__header d-flex align-center">
          <v-select
            class="ml-2 gearchanges__select"
            label="Date"
            :items="dateOptions"
            v-model="selectedDate"
            outlined
            dense
            hide-details
          ></v-select>
          <v-select
            class="ml-2 gearchanges__select"
            label="Venue"
            :items="venueOptions"
            v-model="selectedVenue"
            outlined
            dense
            hide-details
          ></v-select>
          <v-spacer></v-spacer>
          <v-btn class="mr-2" color="primary" outlined small :disabled="loading" @click="print">
            <v-icon small class="mr-1">mdi-printer</v-icon>Print
          </v-btn>
        </header>

        <div style="height: 480px" class="overflow-y-auto mt-2" ref="printArea">
          <div v-if="loading" class="d-flex justify-center align-center" style="height:100%">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="error" class="d-flex justify-center align-center" style="height:100%">
            <span class="text-caption error--text">{{ error }}</span>
          </div>
          <template v-else>
            <v-card v-for="venue in visibleVenues" :key="venue.id" class="pa-2" elevation="0">
              <div class="text-subtitle-1 font-weight-bold">
                {{ venue.name }}<span v-if="venue.state" class="grey--text text-caption ml-2">{{ venue.state }}</span>
              </div>
              <v-card class="box-shadow--container">
                <v-simple-table v-if="venue.rows.length > 0" class="gearchanges__table" dense>
                  <thead>
                    <tr>
                      <th style="width: 8%" class="text-left">Race</th>
                      <th style="width: 30%" class="text-left">Runner</th>
                      <th class="text-left">Gear Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in venue.rows" :key="`${venue.id}-${row.raceNumber}-${row.tabNo}`" :class="row.scratched ? 'gearchanges__scratched' : ''">
                      <td class="text-left">R{{ row.raceNumber }}</td>
                      <td class="text-left">{{ row.tabNo }}. {{ row.name }}<span v-if="row.scratched" class="error--text text-caption ml-2">SCR</span></td>
                      <td class="text-left">{{ row.gear }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <v-card v-else elevation="0" class="pa-2 d-flex justify-center align-center" height="32px">
                  <span class="text-caption ma-0 grey--text text-sm-body-2">No Gear Change info available for this meeting.</span>
                </v-card>
              </v-card>
            </v-card>

            <v-card elevation="0" v-if="visibleVenues.length === 0" class="pa-2 d-flex justify-center align-center" height="32px">
              <span class="text-caption ma-0 grey--text text-sm-body-2">No Gear Change info available for this date.</span>
            </v-card>
          </template>
        </div>
      </v-container>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { addDays, format, startOfDay } from 'date-fns'
import { apolloClient } from '../vue-apollo'
import { queryGearChanges } from '../common/queries'

const ALL_VENUES = 'All Venues'

export default {
  name: 'GearChanges',
  data: () => ({
    loading: false,
    error: null,
    venues: [],
    selectedDate: startOfDay(new Date()).getTime(),
    selectedVenue: ALL_VENUES
  }),
  computed: {
    dateOptions () {
      const today = startOfDay(new Date())
      return [...Array(7).keys()].map(i => {
        const d = addDays(today, i)
        return { text: format(d, 'EEE dd/MM/yyyy'), value: d.getTime() }
      })
    },
    venueOptions () {
      return [ALL_VENUES, ...this.venues.map(v => v.name)]
    },
    visibleVenues () {
      if (this.selectedVenue === ALL_VENUES) {
        // A full day has ~80 meetings; listing every empty one buries the
        // content, so All Venues only shows meetings that have gear changes.
        return this.venues.filter(v => v.rows.length > 0)
      }
      return this.venues.filter(v => v.name === this.selectedVenue)
    }
  },
  mounted () {
    this.fetchGearChanges()
  },
  watch: {
    selectedDate () {
      this.selectedVenue = ALL_VENUES
      this.fetchGearChanges()
    }
  },
  methods: {
    close () {
      this.$modal.hide('gear-changes')
    },
    async fetchGearChanges () {
      this.loading = true
      this.error = null
      try {
        const date = JSON.stringify(new Date(this.selectedDate))
        const result = await apolloClient.query({ query: gql`${queryGearChanges(date)}` })
        const meetings = result.data.meetingsDated || []
        this.venues = meetings
          .map(m => ({
            id: m.id,
            name: m.track?.name || 'Unknown',
            state: m.track?.state || '',
            type: m.type,
            rows: (m.races || [])
              .slice()
              .sort((a, b) => a.number - b.number)
              .flatMap(r => (r.competitors || [])
                .filter(c => c.gearChangesDescription)
                .sort((a, b) => a.tabNo - b.tabNo)
                .map(c => ({
                  raceNumber: r.number,
                  tabNo: c.tabNo,
                  name: c.name,
                  gear: c.gearChangesDescription,
                  scratched: !!(c.scratchingType && c.scratchingType !== 'STARTER')
                })))
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
      } catch (e) {
        this.error = 'Unable to load gear changes. Please try again.'
      }
      this.loading = false
    },
    print () {
      const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const dateLabel = format(new Date(this.selectedDate), 'EEEE dd MMMM yyyy')
      const sections = this.visibleVenues.map(venue => {
        const rows = venue.rows.map(r =>
          `<tr${r.scratched ? ' class="scr"' : ''}><td>R${r.raceNumber}</td><td>${r.tabNo}. ${esc(r.name)}${r.scratched ? ' (SCR)' : ''}</td><td>${esc(r.gear)}</td></tr>`
        ).join('')
        const body = venue.rows.length > 0
          ? `<table><thead><tr><th>Race</th><th>Runner</th><th>Gear Change</th></tr></thead><tbody>${rows}</tbody></table>`
          : '<p class="empty">No Gear Change info available for this meeting.</p>'
        return `<h2>${esc(venue.name)}${venue.state ? ` <small>${esc(venue.state)}</small>` : ''}</h2>${body}`
      }).join('')
      const win = window.open('', '', 'width=900,height=700')
      if (!win) return
      win.document.write(`<!DOCTYPE html><html><head><title>Gear Changes - ${dateLabel}</title><style>
        body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 24px; }
        h1 { font-size: 18px; } h2 { font-size: 14px; margin: 16px 0 4px; } h2 small { color: #666; font-weight: normal; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ccc; padding: 3px 6px; text-align: left; }
        th { background: #eee; }
        tr.scr td { text-decoration: line-through; color: #999; }
        .empty { color: #666; margin: 4px 0 12px; }
      </style></head><body><h1>DynamicOdds - Gear Changes - ${dateLabel}</h1>${sections}</body></html>`)
      win.document.close()
      win.focus()
      win.print()
    }
  }
}
</script>

<style lang="scss">
  .gearchanges {
    overflow-y: hidden;

    &__wrapper {
      background-color: white;
      overflow-y: auto;
    }

    &__select {
      max-width: 220px;
    }

    &__scratched {
      td:nth-child(2), td:nth-child(3) {
        text-decoration: line-through;
        color: #999;
      }
    }
  }
</style>
